import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Row, Col, Statistic, Typography, Popconfirm, InputNumber,
  Badge, Tooltip,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined,
  UserOutlined, ToolOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import type { Unit, UnitStatus } from '../types';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Canonical lowercase status values (match `UnitStatus` in ../types and the
// API/store). The user-facing label for 'available' is "Vacant".
const STATUS_CONFIG: Record<UnitStatus, { color: string; label: string; badge: 'success' | 'warning' | 'error' | 'default' }> = {
  occupied:    { color: 'green',   label: 'Occupied',    badge: 'success' },
  available:   { color: 'blue',    label: 'Vacant',      badge: 'default' },
  maintenance: { color: 'orange',  label: 'Maintenance', badge: 'warning' },
  reserved:    { color: 'purple',  label: 'Reserved',    badge: 'warning' },
};

const Units: React.FC = () => {
  const { fetchUnitsForCondo, fetchOwners, createUnitRecord, updateUnitRecord, deleteUnitRecord, owners } =
    useCondoStore() as any;
  const { currentCondo } = useAuthStore();

  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [form] = Form.useForm();

  const loadUnits = async () => {
    if (!currentCondo?.id) return;
    setLoading(true);
    try {
      // Load units AND owners (condo-scoped) so the Owner/Resident column can
      // resolve owners by unit number when a unit has no explicit ownerId.
      await Promise.all([
        fetchUnitsForCondo(currentCondo.id),
        fetchOwners(currentCondo.id),
      ]);
      // Read from store after fetch
      const storeUnits = useCondoStore.getState().units || [];
      setUnits(storeUnits);
    } catch {
      staticApi.message.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCondo?.id]);

  // Keep local state in sync with store
  useEffect(() => {
    const unsub = useCondoStore.subscribe((state: any) => {
      setUnits(state.units || []);
    });
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    const all: Unit[] = units;
    return all.filter((u: Unit) => {
      const matchSearch = !search ||
        u.unitNumber.toLowerCase().includes(search.toLowerCase()) ||
        String(u.floor).includes(search);
      const matchStatus = statusFilter === 'all' ||
        (u.status || '').toLowerCase() === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [units, search, statusFilter]);

  // Status values drift between sources: the API/store use lowercase
  // ('occupied' | 'available' | 'maintenance' | 'reserved') while this page's
  // STATUS_CONFIG uses uppercase. Normalize before counting so the KPI tiles
  // are not zero. Treat both 'vacant' and 'available' as vacant.
  const stats = useMemo(() => {
    const norm = (s?: string) => (s || '').toLowerCase();
    return {
      total:       units.length,
      occupied:    units.filter((u: Unit) => norm(u.status) === 'occupied').length,
      vacant:      units.filter((u: Unit) => {
        const s = norm(u.status);
        return s === 'vacant' || s === 'available';
      }).length,
      maintenance: units.filter((u: Unit) => norm(u.status) === 'maintenance').length,
    };
  }, [units]);

  const handleOpenCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'available', bedrooms: 1, bathrooms: 1, floor: 1, area: 800 });
    setModalOpen(true);
  };

  const handleOpenEdit = (u: Unit) => {
    setEditing(u);
    form.setFieldsValue({
      unitNumber: u.unitNumber,
      floor: u.floor,
      area: u.area,
      bedrooms: u.bedrooms,
      bathrooms: u.bathrooms,
      status: u.status,
      notes: u.notes,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, condoId: currentCondo?.id };
      if (editing) {
        await updateUnitRecord(editing.id, payload);
        staticApi.message.success(`Unit ${values.unitNumber} updated`);
      } else {
        await createUnitRecord(payload);
        staticApi.message.success(`Unit ${values.unitNumber} created`);
      }
      setModalOpen(false);
    } catch { /* validation */ }
  };

  const handleDelete = async (id: string) => {
    await deleteUnitRecord(id);
    staticApi.message.success('Unit removed');
  };

  // Lookup owner name for a unit. An explicit ownerId match wins; otherwise we
  // fall back to matching by unit number, because `unit.ownerId` is frequently
  // null/unset while the owner record is linked to the unit only by
  // `unitNumber`. `owners` is already condo-scoped (loaded for the current
  // condo), so a unitNumber match is unambiguous within the portfolio.
  const ownerForUnit = (u: Unit) => {
    const list = owners || [];
    // 1) Explicit ownerId link (still wins when present).
    if (u.ownerId) {
      const byId = list.find((o: any) => o.id === u.ownerId || o.userId === u.ownerId || o.ownerId === u.ownerId);
      if (byId) return byId;
    }
    // 2) Fallback: resolve by unit number within the condo-scoped owners array.
    if (u.unitNumber) {
      const byUnit = list.find((o: any) => o.unitNumber === u.unitNumber);
      if (byUnit) return byUnit;
    }
    return null;
  };

  const columns: ColumnsType<Unit> = [
    {
      title: 'Unit',
      key: 'unit',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: 15 }}>Unit {r.unitNumber}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>Floor {r.floor}</Text>
        </Space>
      ),
      sorter: (a, b) => a.unitNumber.localeCompare(b.unitNumber),
    },
    {
      title: 'Details',
      key: 'details',
      render: (_, r) => (
        <Space size={8}>
          <Tag>{r.bedrooms} Bed</Tag>
          <Tag>{r.bathrooms} Bath</Tag>
          <Tag>{r.area} sq ft</Tag>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s: string) => {
        // Tolerate legacy uppercase rows by lowercasing before lookup.
        const cfg = STATUS_CONFIG[(s || '').toLowerCase() as UnitStatus] || STATUS_CONFIG.available;
        return <Badge status={cfg.badge} text={cfg.label} />;
      },
      filters: Object.entries(STATUS_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (value, record) => (record.status || '').toLowerCase() === value,
    },
    {
      title: 'Owner / Resident',
      key: 'owner',
      render: (_, r) => {
        const owner = ownerForUnit(r);
        if (owner) {
          return (
            <Space direction="vertical" size={1}>
              <Space><UserOutlined /><Text>{owner.name}</Text></Space>
              <Text type="secondary" style={{ fontSize: 12 }}>{owner.email}</Text>
            </Space>
          );
        }
        return <Text type="secondary">Unassigned</Text>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit unit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(r)} />
          </Tooltip>
          <Popconfirm
            title="Delete this unit?"
            onConfirm={() => handleDelete(r.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <HomeOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            Unit Management
          </Title>
          <Text type="secondary">Manage all residential units — occupancy, details, and owner assignments</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Add Unit
        </Button>
      </div>

      {/* KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Total Units" value={stats.total} prefix={<HomeOutlined />} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Occupied" value={stats.occupied} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Vacant" value={stats.vacant} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="In Maintenance"
              value={stats.maintenance}
              valueStyle={{ color: stats.maintenance > 0 ? '#fa8c16' : undefined }}
              prefix={<ToolOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Space style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by unit # or floor…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 240 }}
          allowClear
        />
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
          <Option value="all">All Statuses</Option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <Option key={k} value={k}>{v.label}</Option>
          ))}
        </Select>
      </Space>

      {/* Table */}
      <Card>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showTotal: (t) => `${t} units` }}
          expandable={{
            expandedRowRender: (r) => r.notes ? (
              <div style={{ padding: '4px 0' }}>
                <Text type="secondary">Notes: </Text><Text>{r.notes}</Text>
              </div>
            ) : null,
            rowExpandable: (r) => !!r.notes,
          }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editing ? `Edit Unit ${editing.unitNumber}` : 'Add New Unit'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Save Changes' : 'Create Unit'}
        width={520}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}>
                <Input placeholder="e.g. 401" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="floor" label="Floor" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} max={200} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="bedrooms" label="Bedrooms" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} max={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bathrooms" label="Bathrooms" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} max={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="area" label="Area (sq ft)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={100} max={10000} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <Option key={k} value={k}>{v.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Any notes about this unit…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Units;
